import { createI18n } from "vue-i18n";
import directus from '../composables/useDirectus.js';

const CACHE_KEY = 'orbit_translations';
const CACHE_TIME_KEY = 'orbit_translations_time';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour — refresh translations in background after this

/**
 * Get cached translations from localStorage
 */
function getCachedTranslations() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    // Silently fail - will load from Directus
  }
  return null;
}

/**
 * Check if cache is stale (older than TTL)
 */
function isCacheStale() {
  try {
    const time = localStorage.getItem(CACHE_TIME_KEY);
    if (!time) return true;
    return (Date.now() - parseInt(time, 10)) > CACHE_TTL_MS;
  } catch (e) {
    return true;
  }
}

/**
 * Save translations to localStorage cache
 */
function setCachedTranslations(translations) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(translations));
    localStorage.setItem(CACHE_TIME_KEY, String(Date.now()));
  } catch (e) {
    // Silently fail - cache is optional
  }
}

/**
 * Clear translation cache
 */
export function clearTranslationCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIME_KEY);
  } catch (e) {
    // Silently fail
  }
}

/**
 * Transform Directus translations to vue-i18n format
 * Key = English text, translations contain other languages
 */
function transformTranslations(items) {
  const messages = {
    'en-EN': {},
    'de-DE': {}
  };

  for (const item of items) {
    if (!item.key) continue;

    // Key is the English translation
    messages['en-EN'][item.key] = item.key;

    // Get German translation from nested translations
    if (item.translations && item.translations.length > 0) {
      for (const trans of item.translations) {
        if (trans.languages_code === 'de-DE' && trans.translation) {
          messages['de-DE'][item.key] = trans.translation;
        }
      }
    }

    // Fallback to English if no German translation
    if (!messages['de-DE'][item.key]) {
      messages['de-DE'][item.key] = item.key;
    }
  }

  return messages;
}

/**
 * Load translations from Directus
 */
async function loadTranslationsFromDirectus() {
  try {
    const response = await directus.items('orbit_translations').readByQuery({
      limit: -1,
      fields: ['key', 'translations.languages_code', 'translations.translation']
    });

    if (response.data && response.data.length > 0) {
      const messages = transformTranslations(response.data);
      setCachedTranslations(messages);
      // Refresh the existing-keys cache from the same response — keeps both
      // caches in sync and removes the need for a separate keys-only GET.
      existingKeysCache = new Set(
        response.data.filter(t => t.key).map(t => t.key.toLowerCase())
      );
      existingKeysCacheTime = Date.now();
      return messages;
    }
  } catch (e) {
    // Silently fail - will use cached or fallback
  }
  return null;
}

/**
 * Queue for missing translation keys to batch report
 */
let missingKeysQueue = new Set();
let missingKeysTimeout = null;

// Cache of existing translation keys (lowercase) to avoid repeated DB queries.
// Seeded from `cachedMessages` at module load (see below) so we don't refetch
// the full key list when localStorage already has it.
let existingKeysCache = null;
let existingKeysCacheTime = 0;
let existingKeysFetchPromise = null; // dedupes concurrent in-flight fetches
const CACHE_TTL = 60 * 60 * 1000; // 1 hour — matches translations cache TTL

async function ensureExistingKeysCache() {
  const now = Date.now();
  if (existingKeysCache && (now - existingKeysCacheTime) <= CACHE_TTL) return;
  if (existingKeysFetchPromise) return existingKeysFetchPromise;

  existingKeysFetchPromise = (async () => {
    try {
      const existingResponse = await directus.items('orbit_translations').readByQuery({
        fields: ['key'],
        limit: -1
      });
      existingKeysCache = new Set((existingResponse.data || []).map(t => t.key.toLowerCase()));
      existingKeysCacheTime = Date.now();
    } finally {
      existingKeysFetchPromise = null;
    }
  })();
  return existingKeysFetchPromise;
}

/**
 * Report missing translations to Directus (batched)
 */
async function reportMissingTranslations() {
  if (missingKeysQueue.size === 0) return;

  const keysToReport = Array.from(missingKeysQueue);
  missingKeysQueue.clear();

  try {
    await ensureExistingKeysCache();
    if (!existingKeysCache) return;

    // Filter out keys that already exist (case-insensitive)
    const newKeys = keysToReport.filter(key => !existingKeysCache.has(key.toLowerCase()));

    if (newKeys.length === 0) return;

    // Create missing translations (key = English text)
    for (const key of newKeys) {
      try {
        await directus.items('orbit_translations').createOne({
          key: key
        });
        // Add to cache to prevent re-creating
        existingKeysCache.add(key.toLowerCase());
      } catch (e) {
        // Ignore duplicates - add to cache anyway
        existingKeysCache.add(key.toLowerCase());
      }
    }

  } catch (e) {
    // Silently fail - missing keys will be reported on next attempt
  }
}

/**
 * Queue a missing key for reporting
 */
function queueMissingKey(key) {
  if (!key || key.startsWith('{') || key.startsWith('$')) return;

  missingKeysQueue.add(key);

  if (missingKeysTimeout) {
    clearTimeout(missingKeysTimeout);
  }
  missingKeysTimeout = setTimeout(reportMissingTranslations, 5000);
}

// Try to load from cache immediately
const cachedMessages = getCachedTranslations();

// Seed the existing-keys cache from the cached translations. This means a
// freshly loaded tab reuses the keys it already has in localStorage instead
// of re-fetching `/items/orbit_translations?fields=key&limit=-1` again.
if (cachedMessages && cachedMessages['en-EN']) {
  try {
    const cachedTime = parseInt(localStorage.getItem(CACHE_TIME_KEY) || '0', 10);
    existingKeysCache = new Set(
      Object.keys(cachedMessages['en-EN']).map(k => k.toLowerCase())
    );
    existingKeysCacheTime = cachedTime || Date.now();
  } catch (_) {
    // If anything fails, leave the cache null and let the first report fetch.
  }
}

// Create i18n instance
const i18n = createI18n({
  warnHtmlMessage: false,
  legacy: false,
  locale: "en-EN",
  fallbackLocale: "en-EN",
  fallbackWarn: false,
  missingWarn: false,
  messages: cachedMessages || {
    "de-DE": {},
    "en-EN": {}
  },
  missing(locale, key) {
    queueMissingKey(key);
  },
});

/**
 * Initialize translations - loads from Directus on login or if cache is empty/stale
 * Uses stale-while-revalidate: serve cached immediately, refresh in background if stale
 * @param {boolean} isLogin - Set to true on login to force refresh
 */
export async function initTranslations(isLogin = false) {
  // Force refresh on login
  if (isLogin) {
    const fresh = await loadTranslationsFromDirectus();
    if (fresh) {
      i18n.global.setLocaleMessage('en-EN', fresh['en-EN']);
      i18n.global.setLocaleMessage('de-DE', fresh['de-DE']);
    }
    return;
  }

  // No cache at all — must fetch before continuing
  if (!cachedMessages) {
    const fresh = await loadTranslationsFromDirectus();
    if (fresh) {
      i18n.global.setLocaleMessage('en-EN', fresh['en-EN']);
      i18n.global.setLocaleMessage('de-DE', fresh['de-DE']);
    }
    return;
  }

  // Cache exists — use it immediately, refresh in background if stale
  if (isCacheStale()) {
    loadTranslationsFromDirectus().then((fresh) => {
      if (fresh) {
        i18n.global.setLocaleMessage('en-EN', fresh['en-EN']);
        i18n.global.setLocaleMessage('de-DE', fresh['de-DE']);
      }
    });
  }
}

/**
 * Force refresh translations from Directus
 */
export async function refreshTranslations() {
  clearTranslationCache();
  const fresh = await loadTranslationsFromDirectus();
  if (fresh) {
    i18n.global.setLocaleMessage('en-EN', fresh['en-EN']);
    i18n.global.setLocaleMessage('de-DE', fresh['de-DE']);
  }
}

export default i18n;
